import styles from 'css/src/components/common/AnnouncementCard.module.scss';

import React from 'react';

import TimeAgo from 'js/src/components/common/ui/TimeAgo';
import { trackAnnouncementCtaClick } from 'js/src/data/announcements';
import type { ReadonlyAnnouncement } from 'js/src/data/announcements/announcement-types';
import { useAppDispatch } from 'js/src/libs/RootState';

function getYouTubeEmbedUrl(url: string): string {
    if (!url) return url;
    if (url.includes('/embed/')) return url;
    const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (watchMatch) {
        return `https://www.youtube.com/embed/${watchMatch[1]}`;
    }
    return url;
}

type AnnouncementCardProps = {
    announcement: ReadonlyAnnouncement;
    onCtaClick?: () => void;
    titleTag?: 'h2' | 'h3' | 'h4';
};

export const AnnouncementCard: React.FC<AnnouncementCardProps> = React.memo(
    function AnnouncementCard({ announcement, onCtaClick, titleTag: TitleTag = 'h4' }) {
        const dispatch = useAppDispatch();
        const id = announcement.id;
        const title = announcement.title;
        const body = announcement.body;
        const imageUrl = announcement.image_url;
        const videoUrl = announcement.video_url;
        const ctaText = announcement.cta_text;
        const ctaUrl = announcement.cta_url;
        const category = announcement.category;
        const publishDate = announcement.publish_date;
        const isPreview = announcement.is_preview;

        const handleCtaClick = React.useCallback(() => {
            dispatch(trackAnnouncementCtaClick(id));
            if (ctaUrl) {
                window.open(ctaUrl, '_blank', 'noopener,noreferrer');
            }
            onCtaClick?.();
        }, [dispatch, id, ctaUrl, onCtaClick]);

        return (
            <div>
                {isPreview && (
                    <div className={styles['announcement-card__preview-badge']}>
                        <i translate="no" className="material-symbols-outlined me-l5-4">
                            visibility
                        </i>
                        Preview - Only visible to you
                    </div>
                )}

                <div className={styles['announcement-card__header']}>
                    {category && (
                        <span
                            className={styles['announcement-card__category']}
                            style={{ backgroundColor: category.color }}
                        >
                            {category.name}
                        </span>
                    )}
                    <TimeAgo time={publishDate} className={styles['announcement-card__date']} />
                </div>

                <TitleTag className="announcement-card__title">{title}</TitleTag>

                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt=""
                        className="announcement-card__image"
                        loading="lazy"
                    />
                )}

                {videoUrl && (
                    <div className={styles['announcement-card__video']}>
                        <iframe
                            src={getYouTubeEmbedUrl(videoUrl)}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                )}

                <div
                    className="announcement-card__body"
                    dangerouslySetInnerHTML={{ __html: body }}
                />

                {ctaText && ctaUrl && (
                    <button
                        type="button"
                        className={styles['announcement-card__cta']}
                        onClick={handleCtaClick}
                    >
                        {ctaText}
                    </button>
                )}
            </div>
        );
    },
);
